import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
  MenuItem,
  Divider,
  Container,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Search,
  Business,
  LocalPharmacy,
} from '@mui/icons-material';
import { medicinesAPI, suppliersAPI, purchaseAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

export const EditPurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [poNumber, setPoNumber] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [purchaseRes, suppliersRes, medicinesRes] = await Promise.all([
        purchaseAPI.getById(id),
        suppliersAPI.getAll(),
        medicinesAPI.getAll()
      ]);

      const purchaseOrder = purchaseRes.data.data.purchaseOrder;
      setSuppliers(suppliersRes.data.data.suppliers);
      setMedicines(medicinesRes.data.data.medicines);

      // Set form data from existing purchase order
      setSelectedSupplier(purchaseOrder.supplier);
      setPoNumber(purchaseOrder.poNumber);
      setStatus(purchaseOrder.status);
      setNotes(purchaseOrder.notes || '');
      setExpectedDelivery(
        purchaseOrder.expectedDeliveryDate 
          ? purchaseOrder.expectedDeliveryDate.split('T')[0] 
          : ''
      );

      // Transform items to match the create page structure
      const transformedItems = purchaseOrder.items.map(item => ({
        medicine: item.medicine,
        quantity: item.quantity,
        costPrice: item.costPrice,
        sellingPrice: item.sellingPrice,
        mrp: item.mrp,
        taxRate: item.taxRate,
      }));

      setItems(transformedItems);
    } catch (error) {
      enqueueSnackbar('Error fetching data', { variant: 'error' });
      navigate('/hospital/pharmacy/purchases');
    } finally {
      setLoading(false);
    }
  };

  const addItem = (medicine) => {
    const existingItem = items.find(item => item.medicine._id === medicine._id);
    
    if (existingItem) {
      setItems(items.map(item =>
        item.medicine._id === medicine._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setItems([...items, {
        medicine,
        quantity: 1,
        costPrice: 0,
        sellingPrice: 0,
        mrp: 0,
        taxRate: 18,
      }]);
    }
    
    setSearchTerm('');
    enqueueSnackbar(`${medicine.name} added to order`, { variant: 'success' });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedSupplier) {
      newErrors.supplier = 'Supplier is required';
    }
    
    if (items.length === 0) {
      newErrors.items = 'At least one item is required';
    }
    
    // Validate item prices
    items.forEach((item, index) => {
      if (item.costPrice <= 0) {
        newErrors[`item_${index}_costPrice`] = 'Cost price must be greater than 0';
      }
      if (item.sellingPrice <= 0) {
        newErrors[`item_${index}_sellingPrice`] = 'Selling price must be greater than 0';
      }
      if (item.mrp <= 0) {
        newErrors[`item_${index}_mrp`] = 'MRP must be greater than 0';
      }
      if (item.sellingPrice > item.mrp) {
        newErrors[`item_${index}_pricing`] = 'Selling price cannot exceed MRP';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const removeItem = (medicineId) => {
    setItems(items.filter(item => item.medicine._id !== medicineId));
  };

  const updateItem = (medicineId, field, value) => {
    setItems(items.map(item =>
      item.medicine._id === medicineId
        ? { ...item, [field]: parseFloat(value) || 0 }
        : item
    ));
  };

  const updateQuantity = (medicineId, quantity) => {
    if (quantity < 1) {
      removeItem(medicineId);
      return;
    }
    
    setItems(items.map(item =>
      item.medicine._id === medicineId
        ? { ...item, quantity }
        : item
    ));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.costPrice), 0);
    const tax = items.reduce((sum, item) => sum + (item.quantity * item.costPrice * (item.taxRate / 100)), 0);
    const grandTotal = subtotal + tax;
    
    return { subtotal, tax, grandTotal };
  };

  const handleUpdateOrder = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const totals = calculateTotals();
      const orderData = {
        supplier: selectedSupplier._id,
        items: items.map(item => ({
          medicine: item.medicine._id,
          quantity: item.quantity,
          costPrice: item.costPrice,
          sellingPrice: item.sellingPrice,
          mrp: item.mrp,
          taxRate: item.taxRate,
        })),
        expectedDeliveryDate: expectedDelivery || undefined,
        notes: notes || undefined,
        status: status,
        poNumber: poNumber,
        totalAmount: totals.subtotal,
        taxAmount: totals.tax,
        grandTotal: totals.grandTotal,
        updatedBy: user._id
      };

      await purchaseAPI.update(id, orderData);
      enqueueSnackbar('Purchase order updated successfully!', { variant: 'success' });
      navigate('/hospital/pharmacy/purchases');
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error.response?.data?.message || 'Error updating purchase order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, tax, grandTotal } = calculateTotals();

  const filteredMedicines = medicines.filter(medicine =>
    medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicine.genericName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h4">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBack sx={{ fontSize: 28 }} />}
              onClick={() => navigate('/hospital/pharmacy/purchases')}
              sx={{ fontSize: '1.1rem' }}
            >
              Back
            </Button>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              Edit Purchase Order: {poNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => navigate('/hospital/pharmacy/purchases')}
              variant="outlined"
              sx={{ fontSize: '1.1rem', py: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              startIcon={<Save sx={{ fontSize: 24 }} />}
              variant="contained"
              onClick={handleUpdateOrder}
              disabled={saving || !selectedSupplier || items.length === 0}
              sx={{ fontSize: '1.1rem', py: 1.5, px: 3 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Supplier & Medicine Search */}
          <Grid item xs={12} md={8}>
            {/* Supplier Selection */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Supplier Information
                </Typography>
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => 
                    `${option.name} (${option.contactPerson})`
                  }
                  value={selectedSupplier}
                  onChange={(event, newValue) => setSelectedSupplier(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Supplier"
                      placeholder="Search supplier by name"
                      sx={{ fontSize: '1.1rem' }}
                      InputProps={{
                        ...params.InputProps,
                        sx: { fontSize: '1.1rem' },
                        startAdornment: (
                          <>
                            <Business sx={{ mr: 1, color: 'text.secondary', fontSize: 28 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                    />
                  )}
                />
                
                {selectedSupplier && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Contact:</strong> {selectedSupplier.contactPerson}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Phone:</strong> {selectedSupplier.phone}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Email:</strong> {selectedSupplier.email}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Lead Time:</strong> {selectedSupplier.leadTime} days
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      <strong>Address:</strong> {selectedSupplier.address?.street}, {selectedSupplier.address?.city}, {selectedSupplier.address?.state} - {selectedSupplier.address?.zipCode}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Medicine Search */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Add Medicines
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search medicines by name or generic name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                  InputProps={{
                    sx: { fontSize: '1.1rem' },
                    startAdornment: (
                      <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 28 }} />
                    ),
                  }}
                />

                {searchTerm && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                    {filteredMedicines.slice(0, 5).map((medicine) => (
                      <Box
                        key={medicine._id}
                        sx={{
                          p: 2,
                          mb: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => addItem(medicine)}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                          {medicine.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {medicine.genericName} • {medicine.strength} • {medicine.form}
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {medicine.brand} • {medicine.manufacturer}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Order Items
                </Typography>
                
                {items.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <LocalPharmacy sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h4" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
                      No items in order. Search and add medicines above.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost Price</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Selling Price</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>MRP</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Tax Rate</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.medicine._id}>
                            <TableCell sx={{ py: 2 }}>
                              <Box>
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  {item.medicine.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {item.medicine.genericName}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {item.medicine.strength} • {item.medicine.form}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <IconButton
                                  size="large"
                                  onClick={() => updateQuantity(item.medicine._id, item.quantity - 1)}
                                  sx={{ fontSize: '1.5rem' }}
                                >
                                  -
                                </IconButton>
                                <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center', fontSize: '1.2rem' }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="large"
                                  onClick={() => updateQuantity(item.medicine._id, item.quantity + 1)}
                                  sx={{ fontSize: '1.5rem' }}
                                >
                                  +
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                type="number"
                                value={item.costPrice}
                                onChange={(e) => updateItem(item.medicine._id, 'costPrice', e.target.value)}
                                sx={{ width: 100, fontSize: '1.1rem' }}
                                inputProps={{ 
                                  min: 0, 
                                  step: 0.01,
                                  style: { fontSize: '1.1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                type="number"
                                value={item.sellingPrice}
                                onChange={(e) => updateItem(item.medicine._id, 'sellingPrice', e.target.value)}
                                sx={{ width: 100, fontSize: '1.1rem' }}
                                inputProps={{ 
                                  min: 0, 
                                  step: 0.01,
                                  style: { fontSize: '1.1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                type="number"
                                value={item.mrp}
                                onChange={(e) => updateItem(item.medicine._id, 'mrp', e.target.value)}
                                sx={{ width: 100, fontSize: '1.1rem' }}
                                inputProps={{ 
                                  min: 0, 
                                  step: 0.01,
                                  style: { fontSize: '1.1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                type="number"
                                value={item.taxRate}
                                onChange={(e) => updateItem(item.medicine._id, 'taxRate', e.target.value)}
                                sx={{ width: 90, fontSize: '1.1rem' }}
                                inputProps={{ 
                                  min: 0, 
                                  max: 100, 
                                  step: 0.01,
                                  style: { fontSize: '1.1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                ₹{(item.quantity * item.costPrice).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <IconButton
                                color="error"
                                onClick={() => removeItem(item.medicine._id)}
                                size="large"
                              >
                                <Delete sx={{ fontSize: 28 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Order Summary */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Order Summary
                </Typography>

                {/* PO Number */}
                <TextField
                  fullWidth
                  label="PO Number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  margin="normal"
                  required
                  sx={{ fontSize: '1.1rem' }}
                  InputProps={{ sx: { fontSize: '1.1rem' } }}
                  InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                />

                {/* Status */}
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  margin="normal"
                  sx={{ fontSize: '1.1rem' }}
                  InputProps={{ sx: { fontSize: '1.1rem' } }}
                  InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                >
                  <MenuItem value="draft" sx={{ fontSize: '1.1rem' }}>Draft</MenuItem>
                  <MenuItem value="sent" sx={{ fontSize: '1.1rem' }}>Sent</MenuItem>
                  <MenuItem value="confirmed" sx={{ fontSize: '1.1rem' }}>Confirmed</MenuItem>
                  <MenuItem value="partially_received" sx={{ fontSize: '1.1rem' }}>Partially Received</MenuItem>
                  <MenuItem value="completed" sx={{ fontSize: '1.1rem' }}>Completed</MenuItem>
                  <MenuItem value="cancelled" sx={{ fontSize: '1.1rem' }}>Cancelled</MenuItem>
                </TextField>

                {selectedSupplier && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Supplier
                    </Typography>
                    <Typography variant="h6" fontWeight="medium" sx={{ fontSize: '1.2rem' }}>
                      {selectedSupplier.name}
                    </Typography>
                  </Box>
                )}

                <TextField
                  fullWidth
                  type="date"
                  label="Expected Delivery"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                  InputLabelProps={{ shrink: true, sx: { fontSize: '1.1rem' } }}
                  sx={{ mb: 2, fontSize: '1.1rem' }}
                  InputProps={{ sx: { fontSize: '1.1rem' } }}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ mb: 2, fontSize: '1.1rem' }}
                  InputProps={{ sx: { fontSize: '1.1rem' } }}
                  InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                />

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{subtotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Tax:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{tax.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>Total:</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.3rem' }}>
                      ₹{grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Alert 
                  severity={
                    status === 'completed' ? 'success' :
                    status === 'cancelled' ? 'error' :
                    status === 'sent' ? 'info' :
                    status === 'confirmed' ? 'warning' : 'info'
                  } 
                  sx={{ 
                    mb: 2,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                >
                  {status === 'draft' && 'This order is currently in draft mode.'}
                  {status === 'sent' && 'This order has been sent to the supplier.'}
                  {status === 'confirmed' && 'This order has been confirmed by the supplier.'}
                  {status === 'partially_received' && 'This order has been partially received.'}
                  {status === 'completed' && 'This order has been completed.'}
                  {status === 'cancelled' && 'This order has been cancelled.'}
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleUpdateOrder}
                  disabled={saving || !selectedSupplier || items.length === 0}
                  sx={{ 
                    py: 2, 
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {saving ? 'Updating...' : `Update Purchase Order - ₹${grandTotal.toFixed(2)}`}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};